"use client";

import { c as _c } from "react/compiler-runtime";
import { api } from "@agroproj/backend/convex/_generated/api";
import { useQuery } from "convex/react";
import { ChevronDown, type LucideIcon } from "lucide-react";
import type { Route } from "next";
import Link from "next/link";
import { usePathname } from "next/navigation";
import * as React from "react";
import { Logo } from "@/components/logo";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@/components/ui/sidebar";
import { buildOrganizationPath } from "@/lib/organization-path";
import { cn } from "@/lib/utils";
import { NavUser } from "./nav-user";
interface SharedNavLinkItem {
  title: string;
  url: string;
  icon: LucideIcon;
  routeCheck: "strict" | "loose";
}

/** Sidebar entry that runs an in-place command, such as opening the support dialog. */
interface SharedNavActionItem {
  title: string;
  icon: LucideIcon;
  onSelect: () => void;
}
type SharedNavItem = SharedNavLinkItem | SharedNavActionItem;
export interface SharedNavGroup {
  label?: string;
  collapsible?: boolean;
  defaultOpen?: boolean;
  /** Bottom groups are pinned above the footer, for utilities such as support and settings. */
  placement?: "bottom";
  items: SharedNavItem[];
}
interface SharedSidebarProps extends React.ComponentProps<typeof Sidebar> {
  routes: SharedNavGroup[];
  matchMode: "organization" | "absolute";
  footerTopContent?: React.ReactNode;
}
interface SharedSidebarNavProps {
  routes: SharedNavGroup[];
  matchMode: "organization" | "absolute";
}
const GROUP_LABEL_CLASSES = "text-muted-foreground px-2 text-xs font-medium tracking-wider uppercase";
export function SharedSidebar(t0) {
  const $ = _c(24);
  let T0;
  let footerTopContent;
  let matchMode;
  let routes;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    const {
      routes: t4,
      matchMode: t5,
      footerTopContent: t6,
      ...props
    } = t0;
    routes = t4;
    matchMode = t5;
    footerTopContent = t6;
    T0 = Sidebar;
    t1 = props;
    t2 = "icon";
    t3 = cn("border-sidebar-border bg-sidebar border-r", props.className);
    $[0] = t0;
    $[1] = T0;
    $[2] = footerTopContent;
    $[3] = matchMode;
    $[4] = routes;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    T0 = $[1];
    footerTopContent = $[2];
    matchMode = $[3];
    routes = $[4];
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
  }
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <SidebarHeader className="border-sidebar-border bg-sidebar relative z-10 border-b px-6 pt-6 pb-4 group-data-[collapsible=icon]:px-2 group-data-[collapsible=icon]:pt-4 group-data-[collapsible=icon]:pb-3"><div className="flex w-full items-center justify-center"><Logo /></div></SidebarHeader>;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== matchMode || $[10] !== routes) {
    t5 = <SidebarContent className="bg-sidebar relative z-10"><SharedSidebarNav matchMode={matchMode} routes={routes} /></SidebarContent>;
    $[9] = matchMode;
    $[10] = routes;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== footerTopContent) {
    t6 = footerTopContent ? <div className="mb-3">{footerTopContent}</div> : null;
    $[12] = footerTopContent;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <NavUser />;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t6) {
    t8 = <SidebarFooter className="border-sidebar-border bg-sidebar relative z-10 border-t p-3 group-data-[collapsible=icon]:px-2">{t6}{t7}</SidebarFooter>;
    $[15] = t6;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== T0 || $[18] !== t1 || $[19] !== t2 || $[20] !== t3 || $[21] !== t5 || $[22] !== t8) {
    t9 = <T0 {...t1} collapsible={t2} className={t3}>{t4}{t5}{t8}</T0>;
    $[17] = T0;
    $[18] = t1;
    $[19] = t2;
    $[20] = t3;
    $[21] = t5;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  return t9;
}
type NavItemProps = {
  item: SharedNavLinkItem;
  isActive: boolean;
  resolvedHref: string;
} | {
  item: SharedNavActionItem;
  isActive?: never;
  resolvedHref?: never;
};
function NavItem(t0) {
  const $ = _c(22);
  const {
    item,
    isActive: t1,
    resolvedHref
  } = t0;
  const isActive = t1 === undefined ? false : t1;
  const Icon = item.icon;
  const {
    setOpenMobile
  } = useSidebar();
  let t2;
  if ($[0] !== item || $[1] !== resolvedHref || $[2] !== setOpenMobile) {
    t2 = "url" in item ? {
      render: <Link href={resolvedHref as Route} />
    } : {
      type: "button" as const,
      onClick: () => {
        setOpenMobile(false);
        item.onSelect();
      }
    };
    $[0] = item;
    $[1] = resolvedHref;
    $[2] = setOpenMobile;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = isActive ? "page" : undefined;
  const t4 = item.title;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = cn("relative h-8 w-full justify-start gap-2 rounded-md px-2.5 text-xs/relaxed transition-colors duration-150", "not-data-active:text-muted-foreground not-data-active:hover:text-foreground not-data-active:hover:bg-muted/50", "data-active:text-sidebar-active-foreground data-active:bg-sidebar-active data-active:hover:bg-sidebar-active", "group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:gap-0 group-data-[collapsible=icon]:px-2");
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const t6 = isActive ? "text-sidebar-active-foreground" : "text-muted-foreground";
  let t7;
  if ($[5] !== t6) {
    t7 = cn("size-4 shrink-0", t6);
    $[5] = t6;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== Icon || $[8] !== t7) {
    t8 = <Icon className={t7} />;
    $[7] = Icon;
    $[8] = t7;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== item.title) {
    t9 = <span className="min-w-0 flex-1 truncate font-medium group-data-[collapsible=icon]:sr-only">{item.title}</span>;
    $[10] = item.title;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== isActive) {
    t10 = isActive ? <span aria-hidden="true" className="bg-sidebar-active-indicator absolute inset-y-2 left-0 w-0.5 rounded-r-full group-data-[collapsible=icon]:hidden" /> : null;
    $[12] = isActive;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== isActive || $[15] !== item.title || $[16] !== t10 || $[17] !== t2 || $[18] !== t3 || $[19] !== t8 || $[20] !== t9) {
    t11 = <SidebarMenuItem><SidebarMenuButton {...t2} aria-current={t3} isActive={isActive} tooltip={t4} className={t5}>{t8}{t9}{t10}</SidebarMenuButton></SidebarMenuItem>;
    $[14] = isActive;
    $[15] = item.title;
    $[16] = t10;
    $[17] = t2;
    $[18] = t3;
    $[19] = t8;
    $[20] = t9;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  return t11;
}
function SharedSidebarNav(t0) {
  const $ = _c(40);
  const {
    routes,
    matchMode
  } = t0;
  const pathname = usePathname();
  let t1;
  if ($[0] !== matchMode) {
    t1 = matchMode === "organization" ? {} : "skip";
    $[0] = matchMode;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const organizations = useQuery(api.check_membership.listUserOrganizations, t1);
  const {
    isMobile,
    state
  } = useSidebar();
  const isCollapsed = state === "collapsed" && !isMobile;
  let t2;
  if ($[2] !== pathname) {
    t2 = getOrganizationSlugFromPath(pathname);
    $[2] = pathname;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const slugFromPath = t2;
  let t3;
  bb0: {
    if (!organizations) {
      t3 = null;
      break bb0;
    }
    let t4;
    if ($[4] !== organizations) {
      t4 = organizations.find(_temp) ?? organizations[0] ?? null;
      $[4] = organizations;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    t3 = t4;
  }
  const currentOrganization = t3;
  const organizationSlug = matchMode === "organization" ? currentOrganization?.organizationSlug ?? slugFromPath ?? undefined : undefined;
  let t4;
  if ($[6] !== matchMode || $[7] !== organizationSlug) {
    t4 = url => matchMode === "organization" && organizationSlug ? buildOrganizationPath(organizationSlug, url) : url;
    $[6] = matchMode;
    $[7] = organizationSlug;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const resolveTargetPath = t4;
  let t5;
  bb1: {
    if (matchMode === "organization" && organizationSlug) {
      let t6;
      if ($[9] !== organizationSlug || $[10] !== pathname) {
        t6 = getRelativePathForSlug(pathname, organizationSlug);
        $[9] = organizationSlug;
        $[10] = pathname;
        $[11] = t6;
      } else {
        t6 = $[11];
      }
      t5 = t6;
      break bb1;
    }
    t5 = pathname;
  }
  const activePath = t5;
  let t6;
  if ($[12] !== routes) {
    t6 = () => routes.filter(_temp2).map(_temp3);
    $[12] = routes;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const [openSections, setOpenSections] = React.useState(t6);
  let t7;
  let t8;
  if ($[14] !== activePath || $[15] !== routes) {
    t7 = () => {
      routes.forEach(group_1 => {
        if (!group_1.collapsible || !group_1.label) {
          return;
        }
        const hasActiveRoute = group_1.items.some(item => isRouteActive(activePath, item));
        if (!hasActiveRoute) {
          return;
        }
        setOpenSections(prev => prev.includes(group_1.label as string) ? prev : [...prev, group_1.label as string]);
      });
    };
    t8 = [activePath, routes];
    $[14] = activePath;
    $[15] = routes;
    $[16] = t7;
    $[17] = t8;
  } else {
    t7 = $[16];
    t8 = $[17];
  }
  React.useEffect(t7, t8);
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = (sectionLabel, open) => {
      setOpenSections(prev_0 => {
        if (open) {
          return prev_0.includes(sectionLabel) ? prev_0 : [...prev_0, sectionLabel];
        }
        return prev_0.filter(section => section !== sectionLabel);
      });
    };
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const setSectionOpen = t9;
  if (matchMode === "organization" && !organizationSlug) {
    return null;
  }
  let t10;
  if ($[19] !== activePath || $[20] !== resolveTargetPath) {
    t10 = item_0 => "url" in item_0 ? <NavItem isActive={isRouteActive(activePath, item_0)} item={item_0} key={item_0.url} resolvedHref={resolveTargetPath(item_0.url)} /> : <NavItem item={item_0} key={item_0.title} />;
    $[19] = activePath;
    $[20] = resolveTargetPath;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const renderItem = t10;
  let t11;
  if ($[22] !== isCollapsed || $[23] !== openSections || $[24] !== renderItem) {
    t11 = (group_2, groupIndex) => {
      const isCollapsibleGroup = Boolean(group_2.collapsible) && Boolean(group_2.label) && !isCollapsed;
      const isOpen = group_2.label ? openSections.includes(group_2.label) : true;
      return <SidebarGroup className="p-0" key={group_2.label ?? `group-${groupIndex}`}>{isCollapsibleGroup && group_2.label ? <Collapsible onOpenChange={open_0 => setSectionOpen(group_2.label as string, open_0)} open={isOpen}><div className="mt-2 first:mt-0"><CollapsibleTrigger render={<Button className={cn(GROUP_LABEL_CLASSES, "hover:text-foreground flex w-full items-center justify-between")} size="sm" variant="ghost" />}><span>{group_2.label}</span><ChevronDown className={cn("size-3.5 transition-transform", isOpen && "rotate-180")} /></CollapsibleTrigger></div><CollapsibleContent className="mt-1"><SidebarMenu className="pl-1">{group_2.items.map(renderItem)}</SidebarMenu></CollapsibleContent></Collapsible> : <>{!isCollapsed && group_2.label ? <SidebarGroupLabel className={GROUP_LABEL_CLASSES}>{group_2.label}</SidebarGroupLabel> : null}<SidebarGroupContent><SidebarMenu>{group_2.items.map(renderItem)}</SidebarMenu></SidebarGroupContent></>}</SidebarGroup>;
    };
    $[22] = isCollapsed;
    $[23] = openSections;
    $[24] = renderItem;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const renderGroup = t11;
  let t12;
  let t13;
  let t14;
  let t15;
  if ($[26] !== isCollapsed || $[27] !== renderGroup || $[28] !== routes) {
    const topGroups = routes.filter(_temp4);
    const bottomGroups = routes.filter(_temp5);
    t12 = "Navega\xE7\xE3o principal";
    const t16 = isCollapsed ? "p-2" : "p-3";
    if ($[33] !== t16) {
      t13 = cn("relative z-10 flex flex-1 flex-col gap-3 overflow-y-auto", t16);
      $[33] = t16;
      $[34] = t13;
    } else {
      t13 = $[34];
    }
    t14 = topGroups.map(renderGroup);
    t15 = bottomGroups.length > 0 ? <div className="mt-auto flex flex-col gap-3">{bottomGroups.map(renderGroup)}</div> : null;
    $[26] = isCollapsed;
    $[27] = renderGroup;
    $[28] = routes;
    $[29] = t12;
    $[30] = t13;
    $[31] = t14;
    $[32] = t15;
  } else {
    t12 = $[29];
    t13 = $[30];
    t14 = $[31];
    t15 = $[32];
  }
  let t16;
  if ($[35] !== t12 || $[36] !== t13 || $[37] !== t14 || $[38] !== t15) {
    t16 = <nav aria-label={t12} className={t13}>{t14}{t15}</nav>;
    $[35] = t12;
    $[36] = t13;
    $[37] = t14;
    $[38] = t15;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  return t16;
}
function _temp5(group_4) {
  return group_4.placement === "bottom";
}
function _temp4(group_3) {
  return group_3.placement !== "bottom";
}
function _temp3(group_0) {
  return group_0.label as string;
}
function _temp2(group) {
  return group.collapsible && group.label && group.defaultOpen;
}
function _temp(organization) {
  return organization.isActive;
}
function isRouteActive(pathname: string, item: SharedNavItem) {
  if (!("url" in item)) {
    return false;
  }
  return item.routeCheck === "strict" ? normalizeSidebarPath(pathname) === normalizeSidebarPath(item.url) : isLooseMatch(pathname, item.url);
}
function getOrganizationSlugFromPath(pathname: string): string | null {
  const match = pathname.match(/^\/(?:_authenticated\/)?([^/]+)/);
  if (!match) {
    return null;
  }
  const candidate = match[1];
  if (!candidate || ["_authenticated", "sign-in", "sign-up", "waitlist", "app", "onboarding", "admin", "load", "faq", "privacy", "terms"].includes(candidate)) {
    return null;
  }
  return candidate;
}
function getRelativePathForSlug(pathname: string, slug: string): string {
  const normalizedSlug = slug.trim();
  if (!normalizedSlug) {
    return pathname;
  }
  const prefixes = [`/${normalizedSlug}`, `/_authenticated/${normalizedSlug}`];
  for (const prefix of prefixes) {
    if (pathname === prefix) {
      return "/";
    }
    if (pathname.startsWith(`${prefix}/`)) {
      return pathname.slice(prefix.length);
    }
  }
  return pathname;
}
function normalizeSidebarPath(path: string): string {
  if (!path) {
    return "/";
  }
  if (path === "/") {
    return "/";
  }
  return path.endsWith("/") ? path.slice(0, -1) : path;
}
function isLooseMatch(pathname: string, candidate: string): boolean {
  const normalizedPath = normalizeSidebarPath(pathname);
  const normalizedCandidate = normalizeSidebarPath(candidate);
  if (normalizedCandidate === "/") {
    return normalizedPath === "/";
  }
  return normalizedPath === normalizedCandidate || normalizedPath.startsWith(`${normalizedCandidate}/`);
}